import useSWR from 'swr';
import { taskSnapshotMetadataZod } from 'packages/lib/api/annotations/contract/task-snapshot';
import { useNonNullQueryParams } from 'packages/lib/utils/hooks/use-query';
import { ZodTypeDef, ZodType, z } from 'zod';
import { apiGet } from 'packages/lib/api/annotations/rest';

/** A snapshot is a task's Annotation History. A new 'snapshot' is taken of this history each time it transitions between certain states. */
export const useTaskSnapshotSWR = <T, Z extends ZodTypeDef = ZodTypeDef>(
  taskToken: string | undefined,
  zodType: ZodType<T, Z>
) => {
  const params = new URLSearchParams();
  params.set('taskToken', taskToken ?? '');
  const zod = z.array(
    taskSnapshotMetadataZod.extend({
      snapshot: zodType,
    })
  );
  return useSWR<z.infer<typeof zod>>(
    taskToken ? `/task-snapshots?${params.toString()}` : null,
    async (url: string) => {
      const snapshots = await apiGet(url, zod.parse);
      return snapshots.map((snapshot) => ({
        ...snapshot,
        taskToken: snapshot.taskToken ?? taskToken,
      }));
    }
  );
};

/** Used for Annotation History
 * Will return the snapshot associated with the currently selected snapshot (snapshotToken in URL)
 * @param taskToken - the task token of the sample's current task.
 * @param zodType - the Zod definition for the snapshot field of the Task Snapshot response
 */
export const useCurrentTaskSnapshot = <T, Z extends ZodTypeDef = ZodTypeDef>(
  taskToken: string | undefined,
  zodType: ZodType<T, Z>
) => {
  const urlParamsObj = useNonNullQueryParams<{
    snapshotToken: string | undefined;
  }>();
  const {
    data: taskSnapshots,
    isLoading: isLoadingTaskSnapshots,
    error: taskSnapshotError,
  } = useTaskSnapshotSWR(
    urlParamsObj.snapshotToken ? taskToken : undefined,
    zodType
  );
  const snapshot = taskSnapshots?.find(
    (snapshot) => snapshot.token === urlParamsObj.snapshotToken
  );
  return { isLoadingTaskSnapshots, taskSnapshotError, snapshot };
};

/** Used for Annotation History
 * Will return the previous snapshot that comes prior to the currently selected snapshot (snapshotToken in URL),
 * or undefined if there is no previous snapshot.
 * @param taskToken - the task token of the snapshot to fetch. Can also be 'current' or undefined to not fetch
 * @param zodType - the Zod definition for the snapshot field of the Task Snapshot response
 */
export const usePreviousTaskSnapshot = <T, Z extends ZodTypeDef = ZodTypeDef>(
  taskToken: string | undefined,
  zodType: ZodType<T, Z>
) => {
  const urlParamsObj = useNonNullQueryParams<{
    snapshotToken: string | undefined;
  }>();
  const {
    data: taskSnapshots,
    isLoading: isLoadingPrevSnapshots,
    error: taskSnapshotError,
  } = useTaskSnapshotSWR(
    urlParamsObj.snapshotToken ? taskToken : undefined,
    zodType
  );

  // When user clicks on 'Current', there will be no current snapshot (index -1), so previous snapshot is the last item in the snapshots array
  const currentIndex =
    urlParamsObj.snapshotToken === 'current' && taskSnapshots
      ? taskSnapshots.length
      : taskSnapshots?.findIndex(
          (snapshot) => snapshot.token === urlParamsObj.snapshotToken
        ) ?? 0;
  const prevIndex = currentIndex - 1;
  const prevSnapshot =
    taskSnapshots && prevIndex >= 0 ? taskSnapshots[prevIndex] : undefined;

  return { isLoadingPrevSnapshots, taskSnapshotError, snapshot: prevSnapshot };
};
